import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaTruck,
  FaWeightHanging,
  FaBoxOpen,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaFileUpload,
  FaSpinner,
} from "react-icons/fa"; // Transport-related icons

const TptUpdateModal = ({ isOpen, closeModal, orderId }) => {
  const [transporterName, setTransporterName] = useState("");
  const [numberOfParcels, setNumberOfParcels] = useState(0);
  const [tptCharges, setTptCharges] = useState(0);
  const [weight, setWeight] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [estimatedDate, setEstimatedDate] = useState("");
  const [lrUrl, setLrUrl] = useState(""); // Store the uploaded LR copy URL
  const [loading, setLoading] = useState(false); // Track if the form is loading
  const [toggleState, setToggleState] = useState({ toggle: false });

  const token = localStorage.getItem("token");

  // Fetch TPT details when the modal is opened
  useEffect(() => {
    if (isOpen) {
      const fetchTptDetails = async () => {
        try {
          const response = await fetch(
            buildApiUrl(`/api/orderItem/order/${orderId}/transport/tptdetails`),
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            const { tptDetails } = data;

            // Populate the form with the fetched details
            setTransporterName(
              tptDetails.transporterName || data.transporterName || ""
            );
            setNumberOfParcels(tptDetails.numberOfParcels || 0);
            setTptCharges(tptDetails.tptCharges || 0);
            setWeight(tptDetails.weight || 0);
            setRemarks(tptDetails.remarks || "");
            setEstimatedDate(
              tptDetails.estimatedDate
                ? new Date(tptDetails.estimatedDate).toISOString().slice(0, 16)
                : ""
            );
            setLrUrl(tptDetails.uploadLR || ""); // Set the existing LR URL if available
          } else {
            throw new Error("Failed to fetch transport details");
          }
        } catch (error) {
          toast.error("Error: " + error.message);
        }
      };

      fetchTptDetails();
    }
  }, [isOpen, orderId, token]);

  // Handle LR file upload
  const handleLrUpload = async (e) => {
    const file = e.target.files[0]; // Get the file selected by the user
    if (file) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await fetch(
          buildApiUrl("/api/upload/upload"),
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (response.ok) {
          const data = await response.json();
          setLrUrl(data.fileUrls[0]); // Assuming the API returns the URL of the uploaded file
          toast.success("LR copy uploaded successfully!");
        } else {
          throw new Error("Failed to upload LR copy");
        }
      } catch (error) {
        toast.error("Error: " + error.message);
      }
    }
  };

  // Handle form submission (update transport details)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Set loading state to true

    // If LR copy is not yet uploaded, upload it first
    if (!lrUrl) {
      toast.info("Uploading LR copy, please wait...");
      try {
        await handleLrUpload(e); // Upload the LR copy
      } catch (error) {
        setLoading(false); // Reset loading state
        return; // If the upload fails, stop the submission process
      }
    }

    // Proceed to update the transport details once the LR copy is uploaded
    try {
      const response = await fetch(
        `buildApiUrl("/api/orderItem/order/${orderId}/transport`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            transporterName,
            numberOfParcels,
            tptCharges,
            weight,
            remarks,
            estimatedDate,
            uploadLR: lrUrl, // Include the LR copy URL in the data
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update transport details");
      }

      const data = await response.json();
      toast.success("Transport details updated successfully!");
      closeModal();
    } catch (error) {
      toast.error("Error: " + error.message);
    } finally {
      setLoading(false); // Reset loading state after form submission
    }
  };

  return (
    isOpen && (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-70 z-50">
        <div className="bg-white rounded-lg p-6 w-2/3 shadow-lg border-l-4 border-blue-500">
          <h2 className="text-3xl font-semibold text-blue-600 mb-6 flex items-center">
            <FaTruck className="mr-3 text-4xl" /> Transport Update
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="flex flex-wrap -mx-2 mb-4">
              {/* Transporter Name */}
              <div className="w-full sm:w-1/2 px-2 mb-4">
                <label className="block text-sm font-medium text-gray-700 flex items-center">
                  <FaBoxOpen className="mr-3 text-gray-600" /> Transporter Name
                </label>
                <input
                  type="text"
                  value={transporterName}
                  onChange={(e) => setTransporterName(e.target.value)}
                  placeholder="Enter transporter name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Number of Parcels */}
              <div className="w-full sm:w-1/2 px-2 mb-4">
                <label className="block text-sm font-medium text-gray-700 flex items-center">
                  <FaBoxOpen className="mr-3 text-gray-600" /> Number of Parcels
                </label>
                <input
                  type="number"
                  value={numberOfParcels}
                  onChange={(e) => setNumberOfParcels(e.target.value)}
                  placeholder="Enter number of parcels"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div className="flex flex-wrap -mx-2 mb-4">
              {/* TPT Charges */}
              <div className="w-full sm:w-1/2 px-2 mb-4">
                <label className="block text-sm font-medium text-gray-700 flex items-center">
                  <FaMoneyBillWave className="mr-3 text-gray-600" /> TPT Charges
                </label>
                <input
                  type="number"
                  value={tptCharges}
                  onChange={(e) => setTptCharges(e.target.value)}
                  placeholder="Enter TPT charges"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Weight */}
              <div className="w-full sm:w-1/2 px-2 mb-4">
                <label className="block text-sm font-medium text-gray-700 flex items-center">
                  <FaWeightHanging className="mr-3 text-gray-600" /> Weight (kg)
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="Enter weight in kg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div className="flex flex-wrap -mx-2 mb-4">
              {/* Remarks */}
              <div className="w-full sm:w-1/2 px-2 mb-4">
                <label className="block text-sm font-medium text-gray-700 flex items-center">
                  <FaTruck className="mr-3 text-gray-600" /> Remarks
                </label>
                <input
                  type="text"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Enter remarks"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Estimated Date */}
              <div className="w-full sm:w-1/2 px-2 mb-4">
                <label className="block text-sm font-medium text-gray-700 flex items-center">
                  <FaCalendarAlt className="mr-3 text-gray-600" /> Estimated
                  Date
                </label>
                <input
                  type="datetime-local"
                  value={estimatedDate}
                  onChange={(e) => setEstimatedDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            {/* Upload LR Copy */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 flex items-center">
                <FaFileUpload className="mr-3 text-gray-600" /> Upload LR Copy
              </label>
              <input
                type="file"
                onChange={handleLrUpload}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {lrUrl && (
                <p className="mt-2 text-sm text-gray-600">
                  LR Copy uploaded:{" "}
                  <a
                    href={lrUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600"
                  >
                    View LR Copy
                  </a>
                </p>
              )}
            </div>

            <div className="flex justify-between items-center mt-6">
              <button
                type="button"
                onClick={closeModal}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
              >
                {loading ? (
                  <FaSpinner className="animate-spin text-white" />
                ) : (
                  "Update Details"
                )}
              </button>
            </div>
          </form>
        </div>
        <ToastContainer />
      </div>
    )
  );
};

export default TptUpdateModal;
